import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import captureError from '@disco/utils/captureError'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import upperFirst from 'lodash/upperFirst'
import NavigationService from '@disco/state-management/NavigationService'
import type { EmailCampaign, EmailCampaignId } from 'types/campaign'
import type {
  Campaign,
  CampaignFooter,
  EmailTemplate,
} from 'types/content-editor'
import type { Playlist } from 'types/playlist'
import type { Thunk } from 'types/redux'
import {
  autosave,
  clearAutosaveTimer,
  loadCampaignEditor,
  updateCampaignEditorSection,
} from '@disco/state-management/actions/campaign-editor-actions'
import {
  createCampaignFooter,
  updateCampaignFooter,
} from '@disco/state-management/actions/campaign-footer-actions'
import { campaignListBeginEdit } from '@disco/state-management/actions/campaign-list-actions'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import api from '@disco/api/server'
import {
  CAMPAIGN_BEGIN_SENDING,
  CAMPAIGN_BEGIN_SENDING_FAIL,
  CAMPAIGN_BEGIN_SENDING_SUCCESS,
  CAMPAIGN_DETAILS_RESOLVE_CONFLICT,
  CAMPAIGN_DETAILS_SAVE,
  CAMPAIGN_DETAILS_SAVE_CONFLICT,
  CAMPAIGN_DETAILS_SAVE_FAIL,
  CAMPAIGN_DETAILS_SAVE_SUCCESS,
  CAMPAIGN_DETAILS_UPDATE,
  CAMPAIGN_RESCHEDULED_FAIL,
  CAMPAIGN_RESCHEDULED_SUCCESS,
  CAMPAIGN_SCHEDULE_CANCELLED_FAIL,
  CAMPAIGN_SCHEDULE_CANCELLED_SUCCESS,
  CAMPAIGN_SCHEDULED_TO_SEND_FAIL,
  CAMPAIGN_SCHEDULED_TO_SEND_SUCCESS,
} from '@disco/constants/ActionTypes'
import {
  getCampaign,
  getDefaultCampaignFooter,
} from '@disco/state-management/selectors/campaigns'
import { getContactsFilteredByCampaignRules } from '@disco/state-management/selectors/contacts-all'
import { parseServerError } from '@disco/utils/captureError'
import { generateMarkup } from '@disco/content-editor/default'
import { templates } from '@disco/content-editor/templates'
import { traverseSections } from '@disco/content-editor/section'
import {
  cancelSchedule,
  formatTimezoneOffset,
  getCampaignSenderWithDefaults,
  getTemplateData,
  setTemplateData,
  updateCampaignSchedule,
} from '@disco/utils/emailCampaigns'
import { pushUINotification } from '@disco/utils/notifications'
dayjs.extend(utc)
dayjs.extend(timezone)

type SavedFooter = {
  footer: CampaignFooter | null | undefined
  sectionIndex: number
  saved_id: number | null | undefined
}

const getFooter = (campaignEditor: Campaign): SavedFooter => {
  let footer: SavedFooter | null = null

  for (let index = campaignEditor.sections?.length - 1; index > 0; index--) {
    const section = campaignEditor.sections[index]
    if (!section) {
      continue
    }

    section.columns.forEach(column => {
      if (column.columnContent) {
        const footerBlock = column.columnContent.find(
          content => content.type === 'footer',
        )
        if (footerBlock)
          footer = {
            footer: footerBlock.footer,
            sectionIndex: index,
            saved_id: footerBlock.footer_id,
          }
      }
    })
    if (footer) break
  }

  return (
    footer || {
      footer: null,
      sectionIndex: -1,
      saved_id: null,
    }
  )
}

/**
 * Retrieves a campaign template by its ID.
 * @param templateId - ID of the template to retrieve
 * @returns Email template object or undefined if not found
 */
export function getCampaignTemplate(
  templateId: string,
): EmailTemplate | null | undefined {
  return templates.find(t => t.id === templateId) as EmailTemplate | undefined
}
/**
 * Resets a campaign to use a specific template.
 * @param campaign - Email campaign to reset
 * @param templateId - ID of the template to apply
 * @returns Updated campaign with new template
 */
export function resetCampaignTemplate(
  campaign: EmailCampaign,
  templateId: string,
): EmailCampaign {
  const template = getCampaignTemplate(templateId)
  if (!template) return campaign
  return {
    ...campaign,
    template: template.id,
    template_data: {
      ...getTemplateData(campaign),
      campaignEditor: {
        sections: template.layout,
      },
    },
  }
}
/**
 * Resets a campaign's template and updates the editor.
 * @param campaignId - ID of the campaign to reset
 * @param templateId - ID of the template to apply
 * @returns Thunk that resets campaign template
 */
export function campaignDetailsResetTemplate(
  campaignId: EmailCampaignId,
  templateId: string,
): Thunk {
  return async (dispatch, getState) => {
    try {
      let campaign = getCampaign(getState(), {
        campaignId: campaignId,
      })
      campaign = resetCampaignTemplate(campaign, templateId)
      const template_data = campaign.template_data
      dispatch(
        loadCampaignEditor(
          template_data ? template_data.campaignEditor.sections : [],
        ),
      )
      dispatch({
        type: CAMPAIGN_DETAILS_SAVE_SUCCESS,
        campaignId,
        campaign,
      })
    } catch (error) {
      dispatch({
        type: CAMPAIGN_DETAILS_SAVE_FAIL,
        campaignId,
        error,
      })
    }
  }
}
/**
 * Updates campaign details with basic and extra fields.
 * @param campaignId - ID of the campaign to update
 * @param basicFields - Basic campaign fields to update
 * @param extraFields - Additional fields like subject
 * @returns Thunk that updates campaign details with autosave
 */
export function campaignDetailsUpdate(
  campaignId: EmailCampaignId,
  basicFields: Partial<EmailCampaign>,
  extraFields = {} as {
    subject?: string
  },
): Thunk {
  return async (dispatch, getState) => {
    const campaign = getCampaign(getState(), {
      campaignId: campaignId,
    })
    const templateDataUpdate: { subject?: string } = {}

    if ('subject' in extraFields) {
      templateDataUpdate.subject = extraFields.subject
    }

    let updatedFields = { ...basicFields }

    if (Object.keys(templateDataUpdate).length > 0) {
      // @ts-expect-error - TODO: fix this
      updatedFields = setTemplateData(updatedFields, {
        ...getTemplateData(campaign),
        ...templateDataUpdate,
      })
    }

    return dispatch(
      autosave({
        type: CAMPAIGN_DETAILS_UPDATE,
        campaignId,
        updatedFields,
      }),
    )
  }
}
/**
 * Retrieves a playlist for use in a campaign.
 * @param campaignId - ID of the campaign
 * @param playlistId - ID of the playlist to retrieve
 * @returns Thunk that fetches and sets playlist for campaign
 */
export const getPlaylistForCampaign =
  (campaignId: EmailCampaignId, playlistId: string): Thunk =>
  async dispatch => {
    try {
      const p = await api.getPlaylistVersionsByPlaylistId(parseInt(playlistId))
      const version = p.versions[0]
      const playlist = await api.getPublicPlaylistByPlaylistId(version)
      return await dispatch(campaignPlaylistSet(campaignId, playlist))
    } catch (error) {
      dispatch(sendGenericError(error))
      throw error
    }
  }
/**
 * Sets a playlist for a campaign.
 * @param campaignId - ID of the campaign
 * @param playlist - Playlist object to set
 * @returns Thunk that associates playlist with campaign
 */
export const campaignPlaylistSet =
  (campaignId: EmailCampaignId, playlist: Playlist): Thunk =>
  async (dispatch, getState) => {
    const campaign = getCampaign(getState(), {
      campaignId: campaignId,
    })
    return dispatch({
      type: CAMPAIGN_DETAILS_UPDATE,
      campaignId,
      updatedFields: {
        playlists: { ...campaign.playlists, [playlist.playlist_id]: playlist },
      },
    })
  }
/**
 * Saves campaign details with optional silent mode and sending preparation.
 * @param campaignId - ID of the campaign to save
 * @param silent - Whether to suppress success notifications
 * @param prepareForSending - Whether to prepare campaign for sending
 * @returns Thunk that saves campaign with full processing
 */
export function campaignDetailsSave(
  campaignId: EmailCampaignId,
  silent = false,
  prepareForSending = false,
): Thunk {
  return async (dispatch, getState) => {
    try {
      dispatch({
        type: CAMPAIGN_DETAILS_SAVE,
        campaignId,
      })
      let campaign = getCampaign(getState(), {
        campaignId: campaignId,
      })
      const { filteredContacts, associatedLists } =
        getContactsFilteredByCampaignRules(getState())
      const recipients_list = filteredContacts.map(r => ({
        id: r.id,
        saved_recipients_id: associatedLists[r.id] || null, // `null` is fine too
      }))
      campaign = {
        ...campaign,
        recipients_filter: getState().emailCampaigns.recipientRules,
        recipients_list,
      }
      const { campaignEditor } = getState().emailCampaigns
      const campaignTemplateData = getTemplateData(campaign)
      campaign = setTemplateData(campaign, {
        ...campaignTemplateData,
        versionIndex: (campaignTemplateData.versionIndex || 0) + 1,
        modified: new Date(),
        campaignEditor: campaignEditor,
      })
      const { business } = getState().user
      // render latest mjml
      // const ids = getCampaignPlaylistIds(campaignEditor.sections)
      const campaignData = {
        playlists: campaign.playlists
          ? Object.keys(campaign.playlists).map(
              k => (campaign.playlists || {})[parseInt(k, 10)] as Playlist,
            )
          : [],
        campaignId,
        hostname: get(business, 'hostname', ''),
      }
      const {
        footer: campaignFooter,
        sectionIndex,
        saved_id,
      } = getFooter(campaignEditor)
      const savedFooter = getDefaultCampaignFooter(getState())

      if (campaignFooter && !isEqual(campaignFooter, savedFooter)) {
        if (campaignFooter.id) {
          await dispatch(
            updateCampaignFooter(campaignFooter.id, campaignFooter),
          )
        } else {
          const result = await dispatch(createCampaignFooter(campaignFooter))
          await dispatch(
            updateCampaignEditorSection(sectionIndex, {
              ...campaignEditor.sections[sectionIndex],
              columns: [
                {
                  columnContent: [
                    {
                      footer: result,
                      type: 'footer',
                      footer_id: result.id,
                    },
                  ],
                },
              ],
            }),
          )
        }
      }

      let markupFooter = campaignFooter

      if (!campaignFooter && (saved_id || savedFooter)) {
        markupFooter = savedFooter
      }

      const [mjml, text] = generateMarkup(
        campaignEditor,
        campaignData,
        getState().user.business || null,
        markupFooter,
      )
      // @ts-expect-error - fix later
      campaign = { ...campaign, mjml, text_body: text }
      const snapshot = {
        social_links: markupFooter?.social_links,
        footer_text_mjml: markupFooter?.footer_text_mjml,
        business_address_mjml: business?.business_address_mjml,
      }

      if (prepareForSending) {
        // set sender (required) as sending backend will check
        const { sender_name, sender_email } = getCampaignSenderWithDefaults(
          campaign,
          getState().user,
          getState().emailGroups.emailGroups,
        )
        campaign = {
          ...campaign,
          sender_name,
          sender_email,
          template_data:
            markupFooter && campaign.template_data
              ? {
                  ...campaign.template_data,
                  campaignEditor: {
                    ...campaign.template_data.campaignEditor,
                    sections: traverseSections(
                      campaign.template_data.campaignEditor.sections,
                      element => {
                        if (element.type === 'footer') {
                          return { ...element, snapshot }
                        }

                        return element
                      },
                    ),
                  },
                }
              : campaign.template_data,
        }
      }

      const existingCampaign = await api.getCampaign(campaignId)
      const existingCampaignVer =
        getTemplateData(existingCampaign).versionIndex || 0

      if (existingCampaignVer >= getTemplateData(campaign).versionIndex) {
        const modified =
          getTemplateData(existingCampaign).modified || new Date()
        dispatch({
          type: CAMPAIGN_DETAILS_SAVE_CONFLICT,
          campaignId,
          newerSavedAt: modified,
          newerCampaign: existingCampaign,
        })
        throw new Error('Cannot save: newer campaign exists')
      }

      campaign = await api.updateCampaign(campaignId, campaign)

      if (!silent) {
        pushUINotification({
          message: `${upperFirst(campaign.type)} updated!`,
          // message: `Campaign updated!`,
          // message: `Briefing updated!`,
          level: 'success',
        })
      }

      dispatch({
        type: CAMPAIGN_DETAILS_SAVE_SUCCESS,
        campaignId,
        campaign,
      })
      clearAutosaveTimer(campaignId)
    } catch (error) {
      dispatch({
        type: CAMPAIGN_DETAILS_SAVE_FAIL,
        campaignId,
        error,
      })
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      error.withoutNotification = true
      throw error
    }
  }
}
/**
 * Resolves save conflicts when multiple users edit the same campaign.
 * @param campaignId - ID of the campaign with conflict
 * @param option - Resolution option (load latest or cancel saving)
 * @returns Thunk that resolves the conflict
 */
export function campaignDetailsResolveConflict(
  campaignId: EmailCampaignId,
  option: 'CONFLICT_LOAD_LATEST' | 'CONFLICT_CANCEL_SAVING',
): Thunk {
  return dispatch => {
    dispatch({
      type: CAMPAIGN_DETAILS_RESOLVE_CONFLICT,
      campaignId,
      option,
    })

    if (option === 'CONFLICT_LOAD_LATEST') {
      dispatch(campaignListBeginEdit(campaignId))
    }
  }
}
/**
 * Sends a test email for a campaign to a specified email address.
 * @param campaignId - ID of the campaign to test
 * @param email - Email address to send test to
 * @param mockData - Mock data for testing the email
 * @returns Thunk that sends test email
 */
export function campaignTestSend(
  campaignId: EmailCampaignId,
  email: string,
  mockData: any,
): Thunk {
  return async (dispatch, getState) => {
    try {
      dispatch({
        type: CAMPAIGN_BEGIN_SENDING,
        campaignId,
      })
      const campaign = getCampaign(getState(), {
        campaignId,
      })

      if (campaign.status !== 'sent') {
        await dispatch(campaignDetailsSave(campaignId, true, true))
      }

      await api.sendTestCampaign(campaignId, [email], mockData)
      pushUINotification({
        message: `Sent test email to ${email}!`,
        level: 'success',
      })
      dispatch({
        type: CAMPAIGN_BEGIN_SENDING_SUCCESS,
        campaignId,
      })
    } catch (err) {
      dispatch({
        type: CAMPAIGN_BEGIN_SENDING_FAIL,
        error: err,
        campaignId,
      })

      if (!err.withoutNotification) {
        pushUINotification({
          message: `Could not send test email! Please try again or contact us.`,
          level: 'error',
        })
      }

      throw err
    }
  }
}
/**
 * Begins sending a campaign to all recipients.
 * @param campaignId - ID of the campaign to send
 * @returns Thunk that starts campaign sending process
 */
export function campaignBeginSending(campaignId: EmailCampaignId): Thunk {
  return async (dispatch, getState) => {
    const campaign = getCampaign(getState(), {
      campaignId: campaignId,
    })

    try {
      dispatch({
        type: CAMPAIGN_BEGIN_SENDING,
        campaignId,
      })

      if (campaign.status !== 'sent') {
        await dispatch(campaignDetailsSave(campaignId, true, true))
      }

      await api.sendCampaign(campaignId)
      pushUINotification({
        message: `${upperFirst(campaign.type)} is sending!`,
        level: 'success',
      })
      NavigationService.push(`/campaigns/list`)
      dispatch({
        type: CAMPAIGN_BEGIN_SENDING_SUCCESS,
        campaignId,
      })
    } catch (err) {
      dispatch({
        type: CAMPAIGN_BEGIN_SENDING_FAIL,
        error: err,
        campaignId,
      })
      if (!err.withoutNotification)
        pushUINotification({
          message: `Could not start sending ${campaign.type}! Please try again or contact us.`,
          level: 'error',
        })
    }
  }
}
/**
 * Schedules an email campaign to be sent at a specific time.
 * @param campaignId - ID of the campaign to schedule
 * @param scheduledData - Scheduling data including date and time
 * @param isRescheduled - Whether this is a reschedule operation
 * @returns Thunk that schedules the campaign
 */
export const scheduleSendEmailCampaign = (
  campaignId: EmailCampaignId,
  scheduledData: Record<string, any>,
  isRescheduled: boolean = false,
): Thunk => {
  const { date, time } = scheduledData || {}
  return async (dispatch, getState) => {
    let campaign = getCampaign(getState(), {
      campaignId: campaignId,
    })

    try {
      if (campaign.status !== 'scheduled') {
        await dispatch(campaignDetailsSave(campaignId, true, true))
        // Refresh the campaign data after saving
        campaign = getCampaign(getState(), {
          campaignId: campaignId,
        })
      }

      const updatedCampaign = updateCampaignSchedule(campaign, scheduledData)
      const response = await api.updateCampaign(
        Number(campaignId),
        updatedCampaign,
      )

      if (response && response.scheduled_at && response.scheduled_at_timezone) {
        const timezoneOffset = formatTimezoneOffset(
          response.scheduled_at,
          response.scheduled_at_timezone,
        )
        pushUINotification({
          autoDismiss: 5,
          title: isRescheduled
            ? 'Email rescheduled successfully'
            : 'Email scheduled successfully',
          message: `Your email will go out on ${date} at ${time?.label} (GMT${timezoneOffset})`,
          level: 'success',
        })
        NavigationService.push(`/campaigns/list`)
        dispatch({
          type: isRescheduled
            ? CAMPAIGN_RESCHEDULED_SUCCESS
            : CAMPAIGN_SCHEDULED_TO_SEND_SUCCESS,
          campaignId,
          updatedCampaign,
        })
      }
    } catch (error) {
      dispatch({
        type: isRescheduled
          ? CAMPAIGN_RESCHEDULED_FAIL
          : CAMPAIGN_SCHEDULED_TO_SEND_FAIL,
        error: error,
        campaignId,
      })
      pushUINotification({
        autoDismiss: 5,
        title: isRescheduled
          ? 'Error rescheduling email'
          : 'Error scheduling email',
        message: 'Something went wrong, please try again.',
        level: 'error',
      })
      captureError(error, {
        description: 'Error in scheduleSendEmailCampaign()',
      })
    }
  }
}
/**
 * Cancels a scheduled campaign.
 * @param campaignId - ID of the scheduled campaign to cancel
 * @returns Thunk that cancels campaign scheduling
 */
export const cancelScheduledCampaign = (campaignId: EmailCampaignId): Thunk => {
  return async (dispatch, getState) => {
    const campaign = getCampaign(getState(), {
      campaignId: campaignId,
    })

    try {
      const cancelScheduleCampaign = cancelSchedule(campaign)
      const response = await api.updateCampaign(
        Number(campaignId),
        cancelScheduleCampaign,
      )

      if (response) {
        pushUINotification({
          autoDismiss: 5,
          title: 'Scheduling cancelled!',
          message:
            'Please make sure to re-schedule when you are done editing. ',
          level: 'warning',
        })
        NavigationService.push(`/campaigns/${campaignId}/recipients`)
        dispatch({
          type: CAMPAIGN_SCHEDULE_CANCELLED_SUCCESS,
          campaignId,
        })
      }
    } catch (error) {
      dispatch({
        type: CAMPAIGN_SCHEDULE_CANCELLED_FAIL,
        error: error,
      })
      pushUINotification({
        autoDismiss: 5,
        title: 'Error cancelling email',
        message: 'Something went wrong, please try again.',
        level: 'error',
      })
      captureError(error, { description: 'Error in cancelScheduledCampaign()' })
    }
  }
}
